import { TRANSICOES_ORCAMENTO, type ListarOrcamentosQuery, type OrcamentoResponse, type ResumoOrcamentosQuery } from '@triade/contracts';
import { tamanhoPaginaValido } from '@/components/barra-lista/paginar-lista';

/**
 * Filtros da lista e do kanban. Vivem na URL (`validateSearch`), entao voltar/compartilhar o link preserva a vista.
 * Tudo aqui e apresentacao: filtros e ordenacao rodam no servidor (GET /orcamentos).
 */
export type StatusOrcamento = OrcamentoResponse['status'];
export type Ordenar = ListarOrcamentosQuery['ordenar'];
export type Direcao = ListarOrcamentosQuery['direcao'];
/** Origem do orcamento (filtro `origem` da API). A tela nao tem mais o controle de filtro: a coluna "Origem" da tabela mostra. Links antigos com `?origem=` ainda filtram. */
export type OrigemLista = 'APP' | 'IMPORTADO_XARP';
const ORIGENS: OrigemLista[] = ['APP', 'IMPORTADO_XARP'];

/** Padrao de pagina da lista (S36-D3): 50; a barra oferece 25, 50, 100 e 200. */
export const TAMANHO_PAGINA = 50;

export const STATUS_ORDEM: StatusOrcamento[] = ['RASCUNHO', 'ENVIADO', 'EM_NEGOCIACAO', 'APROVADO', 'PERDIDO'];

/** Periodo de criacao do funil (S36-D5): `ULTIMOS_60` (padrao) ou `TODOS`. Ausente na URL = a escolha lembrada do usuario. */
export type PeriodoFunil = 'ULTIMOS_60' | 'TODOS';

/** Busca da URL: tudo opcional, para `<Link to="/vender/orcamentos">` nao exigir parametros. */
export interface BuscaLista {
  /** So o funil usa: periodo de criacao (ver `funil-periodo.ts`). */
  periodo?: PeriodoFunil;
  status?: StatusOrcamento;
  q?: string;
  clienteId?: string;
  dataDe?: string;
  dataAte?: string;
  abaixo?: boolean;
  /** `true`: so propostas vencidas (derivado da validade da versao de referencia; nao e situacao). */
  vencido?: boolean;
  /** Propostas que vencem nos proximos N dias (1..60, inteiro); ainda nao vencidas. Vem da URL `?vencendo=7`. */
  vencendo?: number;
  origem?: OrigemLista;
  ordenar?: Ordenar;
  direcao?: Direcao;
  pagina?: number;
  /** Registros por pagina (25, 50, 100 ou 200); ausente = TAMANHO_PAGINA. */
  tamanho?: number;
}

const ORDENAR: Ordenar[] = ['numero', 'data', 'cliente', 'total', 'lucro', 'status'];
const DATA = /^\d{4}-\d{2}-\d{2}$/;
const texto = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim().slice(0, 100) : undefined);

/** `validateSearch` das rotas: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBusca(bruto: Record<string, unknown>): BuscaLista {
  const periodo = bruto.periodo === 'TODOS' || bruto.periodo === 'ULTIMOS_60' ? bruto.periodo : undefined;
  const status = STATUS_ORDEM.find((s) => s === bruto.status);
  const ordenar = ORDENAR.find((o) => o === bruto.ordenar);
  const direcao = bruto.direcao === 'asc' || bruto.direcao === 'desc' ? bruto.direcao : undefined;
  const pagina = Number(bruto.pagina);
  const vencendo = Number(bruto.vencendo);
  const dataDe = texto(bruto.dataDe);
  const dataAte = texto(bruto.dataAte);
  const clienteId = texto(bruto.clienteId);
  const q = texto(bruto.q);
  return {
    ...(periodo ? { periodo } : {}),
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
    ...(clienteId && /^[A-Za-z0-9_-]{1,64}$/.test(clienteId) ? { clienteId } : {}),
    ...(dataDe && DATA.test(dataDe) ? { dataDe } : {}),
    ...(dataAte && DATA.test(dataAte) ? { dataAte } : {}),
    ...(bruto.abaixo === true || bruto.abaixo === 'true' ? { abaixo: true } : {}),
    ...(bruto.vencido === true || bruto.vencido === 'true' ? { vencido: true } : {}),
    ...(bruto.vencendo !== undefined && Number.isInteger(vencendo) && vencendo >= 1 && vencendo <= 60 ? { vencendo } : {}),
    ...(ORIGENS.find((o) => o === bruto.origem) ? { origem: bruto.origem as OrigemLista } : {}),
    ...(ordenar ? { ordenar } : {}),
    ...(direcao ? { direcao } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...(tamanhoPaginaValido(bruto.tamanho) !== undefined && tamanhoPaginaValido(bruto.tamanho) !== TAMANHO_PAGINA ? { tamanho: tamanhoPaginaValido(bruto.tamanho) } : {}),
  };
}

/** Parametros de GET /orcamentos para a busca da URL. */
export function paraConsulta(b: BuscaLista): Partial<ListarOrcamentosQuery> {
  const pagina = b.pagina ?? 1;
  const tamanho = b.tamanho ?? TAMANHO_PAGINA;
  return {
    limit: tamanho,
    offset: (pagina - 1) * tamanho,
    ordenar: b.ordenar ?? 'numero',
    direcao: b.direcao ?? 'desc',
    ...(b.status ? { status: b.status } : {}),
    ...paraResumo(b),
  };
}

/** Parametros de GET /orcamentos.xlsx: os mesmos filtros e a mesma ordem da lista, sem paginacao (o servidor exporta tudo). */
export function paraExportacao(b: BuscaLista): Partial<ListarOrcamentosQuery> {
  const { limit: _l, offset: _o, ...resto } = paraConsulta(b);
  return resto;
}

/** Filtros atuais da tela como strings, para os relatorios (docs/RELATORIOS.md). Vazios saem. */
export function paraRelatorio(b: BuscaLista): Record<string, string | undefined> {
  return {
    q: b.q,
    status: b.status,
    dataDe: b.dataDe,
    dataAte: b.dataAte,
    clienteId: b.clienteId,
    abaixoDaMeta: b.abaixo ? 'true' : undefined,
    vencido: b.vencido ? 'true' : undefined,
    vencendo: b.vencendo !== undefined ? String(b.vencendo) : undefined,
    origem: b.origem,
  };
}

/** Filtros que valem para o resumo (tudo menos status, ordenacao e pagina). */
export function paraResumo(b: BuscaLista): Partial<ResumoOrcamentosQuery> {
  return {
    ...(b.q ? { q: b.q } : {}),
    ...(b.clienteId ? { clienteId: b.clienteId } : {}),
    ...(b.dataDe ? { dataDe: b.dataDe } : {}),
    ...(b.dataAte ? { dataAte: b.dataAte } : {}),
    ...(b.abaixo ? { abaixoDaMeta: true } : {}),
    ...(b.vencido ? { vencido: true } : {}),
    ...(b.vencendo !== undefined ? { vencendo: b.vencendo } : {}),
    ...(b.origem ? { origem: b.origem } : {}),
  };
}

/** Ha algum filtro alem de ordenacao/pagina? (decide a mensagem de lista vazia). */
export function temFiltro(b: BuscaLista): boolean {
  return Boolean(b.status || b.q || b.clienteId || b.dataDe || b.dataAte || b.abaixo || b.vencido || b.vencendo !== undefined || b.origem);
}

/** Transicoes aceitas pela API (POST /orcamentos/:id/situacao): a tabela e a do contrato (a API e o banco as impoem). */
export const TRANSICOES: Record<StatusOrcamento, readonly StatusOrcamento[]> = TRANSICOES_ORCAMENTO;
